import "./flows.css";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import React, {useEffect, useState} from "react";
import {MessageTemplateProps} from "../../types/MessageTemplateProps";
import Page from "../../components/page/Page";
import WriteIcon from "../../assets/icons/WriteIcon";
import PresetsIcon from "../../assets/icons/PresetsIcon";
import Skeleton from "../../components/skeleton/Skeleton";
import {Button, Dropdown} from "antd";
import MessageThumb from "../../components/messageThumb/MessageThumb";
import NewNotificationModal from "../../components/modals/notificationModals/NewNotificationModal";
import EditNotificationModal from "../../components/modals/notificationModals/EditNotificationModal";
import PresetsModal from "../../components/modals/presetsModal/PresetsModal";
import CouponsProducts from "../couponsProducts/CouponsProducts";
import {FlowProps} from "../../types/FlowProps";
import {FlowTypeProps} from "../../types/FlowTypeProps";
import {useNavigate, useParams} from "react-router-dom";
import Flows from "./Flows";
import {DownOutlined} from "@ant-design/icons";
import {FlowTriggerProps} from "../../types/FlowTriggerProps";
import {PageButton} from "../../components/page/PageButton";
import {TrashIcon} from "../../assets/icons/TrashIcon";
import {ActivateAllIcon} from "../../assets/icons/ActivateAllIcon";
import {DeactivateAllIcon} from "../../assets/icons/DeactivateAllIcon";
import {DeleteFlow} from "../../api/deleteFlow";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {ActivateAllFlowTemplates} from "../../api/activateAllFlowTemplates";
import {DeactivateAllFlowTemplates} from "../../api/deactivateAllFlowTemplates";
import {EditIcon} from "../../assets/icons/EditIcon";
import RenameFlow from "../../components/modals/flowModals/RenameFlow";
import {ActivateFlow} from "../../api/activateFlow";
import {DeactivateFlow} from "../../api/deactivateFlow";
import StatusDot from "../../components/statusDot/StatusDot";
import EmptyState from "../../components/emptyState/EmptyState";
import WhatsappIntegrationButton from "../../components/whatsappIntegrationButton/WhatsappIntegrationButton";
import ReviewIcon from "../../assets/images/review-icon.png";
import ReviewWarning from "../../components/reviewWarning/ReviewWarning";

const Flow = () => {
    const { data: { templates, flows, flowTypes, flowTriggers, waba }, actions: { loadFlows }  } = useAppContext();
    const { selectedDomain } = useUserContext();
    const [isNewNotificationModalOpen, setIsNewNotificationModalOpen] = useState(false);
    const [isNewFlowModalOpen, setIsNewFlowModalOpen] = useState(false);
    const [isPresetsModalOpen, setIsPresetsModalOpen] = useState(false);
    const [editedNotification, setEditedNotification] = useState<MessageTemplateProps | null>(null);
    const [isCouponManagementOn, setIsCouponManagementOn] = useState(false);
    const [selectedFlowTypeId, setSelectedFlowTypeId] = useState(-1);
    const navigate = useNavigate();
    const params = useParams();
    const flow = flows.data.find((f: FlowProps) => f.id.toString() === params.id) as FlowProps | undefined;
    const flowType = flowTypes.data.find((ft: FlowTypeProps) => ft.id === flow?.flowTypeId) as FlowTypeProps | undefined;
    const flowTypeTriggers = flowTriggers.data.filter((ft: FlowTriggerProps) => ft.flow_type_id === flow?.flowTypeId) as FlowTriggerProps[];
    const flowTrigger = flowTriggers.data.find((ft: FlowTriggerProps) => ft.id === flow?.flowTriggerId) as FlowTriggerProps | undefined;
    const { showToast, closeToast } = useToast();
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

    if(flows.isLoaded && (!flow?.id || !flowType?.id)) navigate('/automations');

    const isEnabled = waba.isLoaded && !!waba?.data?.waba?.whatsappBusinessAccountId;

    const closeEditNotificationModal = () => {
        setEditedNotification(null);
    }

    const onNotificationClick = (template: MessageTemplateProps | null) =>  () => {
        template && setEditedNotification(template);
    }

    const toggleCouponsManagementPanel = () => {
        setIsCouponManagementOn(!isCouponManagementOn);
    }

    const togglePresetsModal = (openCoupons: boolean = false) => {
        setIsPresetsModalOpen(false);
        if(openCoupons) {
            setIsCouponManagementOn(true);
        }
    }

    const checkIfStillPending = () => {
        if(!flow) return;
        const isSomethingPending = flow.templates?.find((template: MessageTemplateProps) => template.whatsappTemplateStatus === "PENDING");
        isSomethingPending && setTimeout(() => {
            console.log("about to reload flows");
            // loadFlows();
            checkIfStillPending();
        }, 30000);
    }

    useEffect(() => {
        if(flows.isLoaded) {
            checkIfStillPending();
        }
    }, [flows.isLoaded]);

    if(!flow?.id || !flowType?.id) return null;

    const activeTemplates = flow.templates?.filter(template => template.active);
    const draftTemplates = flow.templates?.filter(template => !template.active);

    const deleteFlow = async () => {
        selectedDomain && await DeleteFlow(selectedDomain, flow.id);
        loadFlows();
        closeToast();
        navigate('/automations');
    }

    const confirmDeleteFlow = () => {
        showToast({
            skin: ToastSkin.danger,
            title: 'Remove Flow',
            text: 'Are you sure you want to delete this flow and all its templates? This action cannot be undone.',
            buttons: [
                { text: 'Yes', onClick: deleteFlow },
                { text: 'No, Cancel', onClick: closeToast }
            ]
        })
    }

    const callActivateFlow = async () => {
        selectedDomain && await ActivateFlow(selectedDomain, flow.id);
        loadFlows();
    }

    const callDeactivateFlow = async () => {
        selectedDomain && await DeactivateFlow(selectedDomain, flow.id);
        loadFlows();
    }

    const toggleRenameModal = () => {
        setIsRenameModalOpen(!isRenameModalOpen);
    }

    const hasPresets = flowTrigger?.id === 1;

    const createTemplateDropdownOption = {
        items: [{
            key: '1',
            className: 'dropdown-item',
            label: (
                <div onClick={() => setIsNewNotificationModalOpen(true)}>
                    <WriteIcon width={14} height={14} color='rgba(0,0,0,1)' /><span>Create from Scratch</span>
                </div>
            ),
        }, ...(hasPresets ? [{
            key: '2',
            className: 'dropdown-item',
            label: (
                <div onClick={() => setIsPresetsModalOpen(true)}>
                    <PresetsIcon width={14} height={14} color='rgba(0,0,0,1)' /><span>Build with Wizard</span>
                </div>
            ),
        }] : []),]
    };

    return (
        <Page
            pageClassName="flow-page"
            className="flow-page-container"
            title={<div className="title-with-status">{flow.title}<StatusDot status={flow.active ? 'enabled' : 'disabled'} label={flow.active ? 'Active' : 'Disabled'} /></div>}
            breadcrumbs={[{label: 'Automations', pathname: '/automations'}, {label: flow.title}]}
            backButton={true}
            secondaryActions={[{
                label: 'Coupons',
                action: toggleCouponsManagementPanel,
                isDisabled: !isEnabled
            }]}
            burgerItems={[{
                id: "rename-flow",
                label: `Rename ${flow.title}`,
                icon: <EditIcon width={24} />,
                onClick: toggleRenameModal,
                withSeparatorBelow: true,
            }, ...(!flow?.active ? [{
                id: "enable-flow",
                label: "Activate Flow",
                icon: <ActivateAllIcon width={24} />,
                onClick: callActivateFlow,
            }] : []), ...(flow?.active ? [{
                id: "disable-flow",
                label: "Deactivate Automation",
                icon: <DeactivateAllIcon width={24} />,
                onClick: callDeactivateFlow,
            }] : []), {
                id: "remove-flow",
                label: `Remove ${flow.title}`,
                icon: <TrashIcon width={24} />,
                onClick: confirmDeleteFlow,
                withSeparatorAbove: true,
            }
            ]}
        >
            {templates.isLoading || waba.isLoading ? <Skeleton/> :
                <div className="flow-inner">
                    <div className="flow-section">
                        <div className="title">Automation Trigger</div>
                        {/*<div className="description">A Flow Trigger is the event or condition that starts the automated process.</div>*/}
                        <div className="flow-section-content">
                            {flowTypeTriggers?.length > 1 ? <Dropdown menu={{
                                items: flowTypeTriggers.map((ftt: FlowTriggerProps) => ({
                                    key: `flow-trigger-${ftt.id}`,
                                    label: (<a onClick={() => console.log("TODO: Triggers store")}>{ftt.title}</a>)
                                })),
                            }} placement="bottomLeft">
                                <Button className="trigger-selector" icon={<DownOutlined />}>{flowTrigger?.title}</Button>
                            </Dropdown> : <span className="single-trigger-label">{flowTrigger?.title}</span>}
                            <div className="description">
                                {flowTrigger?.description}.
                            </div>
                        </div>
                    </div>
                    <div className="flow-section">
                        <div className="title">
                            <span>Automation Templates</span>
                            <PageButton action={{
                                label: 'Add Template',
                                action: () => setIsNewNotificationModalOpen(true),
                                isDisabled: !isEnabled,
                                options: createTemplateDropdownOption
                            }} />
                        </div>
                        <ReviewWarning />
                        <div className="flow-section-content">
                            {activeTemplates?.length ? <div className="messages-thumbs-container">
                                {activeTemplates?.map((item: MessageTemplateProps, index: number) => <MessageThumb
                                    key={`message-template-${item.id}`}
                                    item={item}
                                    flow={flow}
                                    onNotificationClick={onNotificationClick(item)}
                                    index={index}
                                />)}
                            </div> : <EmptyState title="This automation doesn't have any templates" subTitle="Create one from scratch or use the Automation Wizard to build one quickly." actions={isEnabled ? <>
                                <Button type="primary" onClick={() => setIsNewNotificationModalOpen(true)} size="large" >Create from Scratch</Button>
                                {flowTrigger?.id === 1 ? <Button type="primary" onClick={() => setIsPresetsModalOpen(true)} size="large" >Build with Wizard</Button> : null}
                                </> : <WhatsappIntegrationButton /> } />}
                            {draftTemplates?.length ? <>
                                <div className="sub-title">
                                    Draft Templates
                                </div>
                                <div className="messages-thumbs-container">
                                    {draftTemplates?.map((item: MessageTemplateProps, index: number) => <MessageThumb
                                        key={`message-template-${item.id}`}
                                        item={item}
                                        flow={flow}
                                        onNotificationClick={onNotificationClick(item)}
                                        index={index}
                                        isDraftView={true}
                                    />)}
                                </div>
                            </> : null}
                        </div>
                    </div>
                </div>
            }
            <NewNotificationModal isOpen={isNewNotificationModalOpen} onClose={() => setIsNewNotificationModalOpen(false)} flowId={flow.id} />
            {editedNotification ? <EditNotificationModal editedNotification={editedNotification} onClose={closeEditNotificationModal} flowId={flow.id} flowTriggerId={flow.flowTriggerId} /> : null}
            <PresetsModal isOpen={isPresetsModalOpen} onClose={togglePresetsModal} flowId={flow.id} />
            <CouponsProducts isOpen={isCouponManagementOn} onClose={toggleCouponsManagementPanel} />
            <RenameFlow flowId={flow.id} flowTitle={flow.title} onClose={toggleRenameModal} isOpen={isRenameModalOpen} />
        </Page>
    );
}

export default Flow;